import { computed, ref, type ComputedRef, type Ref } from 'vue';
import type { TouristRoom, TouristsSelection } from '@/entities/searchCriteria';
import type { ChildAgeOption } from './types';
import { CHILD_AGE_OPTIONS } from '../lib/childAgeOptions';
import { formatRoomsLabel, formatTouristsValue } from '../lib/formatTourists';
import {
  MAX_ROOMS,
  MIN_ROOMS,
  addChild,
  addRoom,
  isValidTourists,
  removeChild,
  removeRoom,
  setChildAge,
  setRoomAdults,
  totalTourists,
} from '../lib/touristsRooms';

export interface UseTouristsRooms {
  rooms: ComputedRef<TouristRoom[]>;
  controlActivated: Ref<boolean>;

  selection: ComputedRef<TouristsSelection>;
  roomsLabel: ComputedRef<string>;
  touristsValue: ComputedRef<string>;
  total: ComputedRef<number>;
  canAddRoom: ComputedRef<boolean>;
  canRemoveRoom: ComputedRef<boolean>;
  ageOptions: ChildAgeOption[];

  open: () => void;
  close: () => void;
  toggle: () => void;
  setAdults: (roomIndex: number, value: number) => void;
  addChild: (roomIndex: number, age: number) => void;
  setChildAge: (roomIndex: number, childIndex: number, age: number) => void;
  removeChild: (roomIndex: number, childIndex: number) => void;
  addRoom: () => void;
  removeRoom: (roomIndex: number) => void;
  validate: () => boolean;
}

export interface UseTouristsRoomsOptions {
  model: Ref<TouristsSelection>;
  isVisibleAddButton: () => boolean;
  fieldLabel?: () => string | undefined;
  touristsWordForms?: () => string[] | undefined;
}

export function useTouristsRooms(options: UseTouristsRoomsOptions): UseTouristsRooms {
  const { model } = options;

  const controlActivated = ref(false);

  const selection = computed<TouristsSelection>(() => model.value);
  const rooms = computed<TouristRoom[]>(() => model.value.rooms);
  const total = computed<number>(() => totalTourists(model.value));
  const canAddRoom = computed<boolean>(() => model.value.rooms.length < MAX_ROOMS);
  const canRemoveRoom = computed<boolean>(() => model.value.rooms.length > MIN_ROOMS);
  const roomsLabel = computed<string>(() =>
    formatRoomsLabel(rooms.value.length, options.isVisibleAddButton(), options.fieldLabel?.()),
  );
  const touristsValue = computed<string>(() =>
    formatTouristsValue(total.value, options.isVisibleAddButton(), rooms.value, options.touristsWordForms?.()),
  );

  const open = (): void => {
    controlActivated.value = true;
  };

  const close = (): void => {
    controlActivated.value = false;
  };

  const toggle = (): void => {
    controlActivated.value = !controlActivated.value;
  };

  const setAdults = (roomIndex: number, value: number): void => {
    model.value = setRoomAdults(model.value, roomIndex, value);
  };

  const addChildAction = (roomIndex: number, age: number): void => {
    model.value = addChild(model.value, roomIndex, age);
  };

  const setChildAgeAction = (roomIndex: number, childIndex: number, age: number): void => {
    model.value = setChildAge(model.value, roomIndex, childIndex, age);
  };

  const removeChildAction = (roomIndex: number, childIndex: number): void => {
    model.value = removeChild(model.value, roomIndex, childIndex);
  };

  const addRoomAction = (): void => {
    model.value = addRoom(model.value);
  };

  const removeRoomAction = (roomIndex: number): void => {
    model.value = removeRoom(model.value, roomIndex);
  };

  const validate = (): boolean => isValidTourists(model.value);

  return {
    rooms,
    controlActivated,
    selection,
    roomsLabel,
    touristsValue,
    total,
    canAddRoom,
    canRemoveRoom,
    ageOptions: CHILD_AGE_OPTIONS,
    open,
    close,
    toggle,
    setAdults,
    addChild: addChildAction,
    setChildAge: setChildAgeAction,
    removeChild: removeChildAction,
    addRoom: addRoomAction,
    removeRoom: removeRoomAction,
    validate,
  };
}
